import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Toast } from "primereact/toast";
import {
  deleteUserService,
  getAllusersService,
  updateUserService,
} from "../../services/User/UsersServices";
import { deleteCompanyService } from "../../services/Company/CompanyServices";
import { showError, showSuccess } from "../Toast/Toast.functions";
import AdminPageHeader from "./AdminPageHeader";

interface AdminUser {
  _id: string;
  name: string;
  surname: string;
  email: string;
  userType: number;
  userImage?: { filename: string; key: string };
  companies: { _id: string; name: string }[];
}

const USER_TYPE_OPTIONS = [
  { value: 1, labelKey: "AgriculturalProducer" as const },
  { value: 2, labelKey: "Corporation" as const },
  { value: 3, labelKey: null },
];

const AdminUsers = () => {
  const { t } = useTranslation(["common"]);
  const toast = useRef<Toast>(null);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  });

  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null);

  const userTypeLabel = (userType: number) => {
    const option = USER_TYPE_OPTIONS.find((o) => o.value === userType);
    return option?.labelKey ? t(`common:${option.labelKey}`) : "Administrator";
  };

  const loadUsers = () => {
    setLoading(true);
    getAllusersService()
      .then((res: any) => setUsers(res.data ?? []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const onGlobalFilterChange = (value: string) => {
    setFilters({ global: { value, matchMode: FilterMatchMode.CONTAINS } });
    setGlobalFilterValue(value);
  };

  const submitEdit = () => {
    if (!editingUser) return;
    updateUserService({
      _id: editingUser._id,
      name: editingUser.name,
      surname: editingUser.surname,
      email: editingUser.email,
      userType: editingUser.userType,
      userImage: editingUser.userImage,
      companies: editingUser.companies.map((c) => c._id),
    })
      .then(() => {
        showSuccess(toast, t("common:Save"));
        setEditingUser(null);
        loadUsers();
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const confirmDelete = () => {
    if (!deletingUser) return;
    deleteUserService({ _id: deletingUser._id })
      .then(() => {
        const firstCompanyId = deletingUser.companies?.[0]?._id;
        const afterDelete = () => {
          setDeletingUser(null);
          setUsers((prev) => prev.filter((u) => u._id !== deletingUser._id));
        };
        if (firstCompanyId) {
          deleteCompanyService({ _id: firstCompanyId })
            .then(afterDelete)
            .catch(() => {
              afterDelete();
            });
        } else {
          afterDelete();
        }
      })
      .catch(() => {
        showError(toast, t("common:genericError"));
        setDeletingUser(null);
      });
  };

  return (
    <div className="admin-page">
      <Toast ref={toast} position="top-right" />
      <AdminPageHeader title={t("common:userList")} />

      <div className="admin-page-body">
      <div className="p-input-icon-left mb-3">
        <i className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={(e) => onGlobalFilterChange(e.target.value)}
          placeholder="Keyword Search"
        />
      </div>

      <DataTable
        value={users}
        loading={loading}
        paginator
        rows={20}
        dataKey="_id"
        filters={filters}
        globalFilterFields={["name", "surname", "email"]}
        emptyMessage="Non sono presenti utenti"
        sortField="name"
        sortOrder={1}
      >
        <Column field="name" header={t("common:Name")} sortable />
        <Column field="surname" header={t("common:Surname")} sortable />
        <Column field="email" header={t("common:Email")} sortable />
        <Column
          field="userType"
          header={t("common:type")}
          body={(row: AdminUser) => userTypeLabel(row.userType)}
        />
        <Column
          field="companies"
          header={t("common:companies")}
          body={(row: AdminUser) =>
            (row.companies ?? []).map((c) => c.name).join(", ")
          }
        />
        <Column
          body={(row: AdminUser) => (
            <div className="admin-row-actions">
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text"
                onClick={() => setEditingUser({ ...row })}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-warning"
                onClick={() => setDeletingUser(row)}
              />
            </div>
          )}
        />
      </DataTable>
      </div>

      <Dialog
        header={`${t("common:Name")} / ${t("common:Surname")}`}
        visible={editingUser != null}
        onHide={() => setEditingUser(null)}
        style={{ width: "40vw" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setEditingUser(null)}>
              {t("common:undo")}
            </Button>
            <Button className="general-button" onClick={submitEdit}>
              {t("common:Save")}
            </Button>
          </div>
        }
      >
        {editingUser && (
          <div className="p-fluid">
            <div className="field">
              <label>{t("common:Name")}</label>
              <InputText
                value={editingUser.name}
                onChange={(e) =>
                  setEditingUser({ ...editingUser, name: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:Surname")}</label>
              <InputText
                value={editingUser.surname}
                onChange={(e) =>
                  setEditingUser({ ...editingUser, surname: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:Email")}</label>
              <InputText
                value={editingUser.email}
                onChange={(e) =>
                  setEditingUser({ ...editingUser, email: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:type")}</label>
              <Dropdown
                value={editingUser.userType}
                options={USER_TYPE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.labelKey ? t(`common:${o.labelKey}`) : "Administrator",
                }))}
                optionLabel="label"
                optionValue="value"
                onChange={(e) =>
                  setEditingUser({ ...editingUser, userType: e.value })
                }
              />
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        onHide={() => setDeletingUser(null)}
        visible={deletingUser != null}
        closable={false}
        style={{ maxWidth: "50vh" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setDeletingUser(null)}>
              {t("common:No")}
            </Button>
            <Button className="general-button" onClick={confirmDelete}>
              {t("common:Yes")}
            </Button>
          </div>
        }
      >
        {t("common:confirmDeleteUser")}
        {deletingUser?.name}
      </Dialog>
    </div>
  );
};

export default AdminUsers;
